import React, { useState, useMemo, useCallback } from 'react';
import { useWhatsApp } from '../context/WhatsAppContext';
import {
  Search, Trash2, Download, MessageSquare, Clock, User, X, Filter,
} from 'lucide-react';

const Reports = () => {
  const { incomingLog = [], setIncomingLog } = useWhatsApp();

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all' | 'text' | 'media'

  const filteredLog = useMemo(() => {
    let list = incomingLog;

    if (filterType === 'text') {
      list = list.filter(m => m.text && !m.text.startsWith('['));
    } else if (filterType === 'media') {
      list = list.filter(m => m.text && m.text.startsWith('['));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        (m.senderName && m.senderName.toLowerCase().includes(q)) ||
        (m.sender && m.sender.toLowerCase().includes(q)) ||
        (m.from && m.from.toLowerCase().includes(q)) ||
        (m.text && m.text.toLowerCase().includes(q))
      );
    }

    return list;
  }, [incomingLog, search, filterType]);

  const handleClearAll = useCallback(() => {
    if (window.confirm('Clear all message logs? This cannot be undone.')) {
      setIncomingLog([]);
    }
  }, [setIncomingLog]);

  const handleExportCSV = useCallback(() => {
    if (incomingLog.length === 0) return;
    const headers = ['Timestamp', 'Sender', 'Phone', 'Message', 'Status'];
    const rows = incomingLog.map(m => [
      m.timestamp ? new Date(m.timestamp * 1000).toLocaleString() : '',
      m.senderName || m.sender || '',
      m.from?.split('@')[0] || '',
      (m.text || '').replace(/,/g, ';'),
      'received',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp-messages-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [incomingLog]);

  const uniqueSenders = new Set(incomingLog.map(m => m.sender || m.from)).size;
  const latestMsg = incomingLog[0];

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      {/* ── Header ── */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="text-green-600" size={28} />
            Message Reports
          </h1>
          <p className="mt-1 text-slate-500 text-sm">
            Real-time log of all received WhatsApp messages
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            disabled={incomingLog.length === 0}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm border border-gray-200 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={15} /> Export CSV
          </button>
          <button
            onClick={handleClearAll}
            disabled={incomingLog.length === 0}
            className="inline-flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2 text-sm font-medium text-red-600 border border-red-100 hover:bg-red-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 size={15} /> Clear All
          </button>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-blue-100 p-2.5">
              <MessageSquare size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Messages</p>
              <p className="text-2xl font-bold text-slate-900">{incomingLog.length}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-green-100 p-2.5">
              <User size={18} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Unique Senders</p>
              <p className="text-2xl font-bold text-slate-900">{uniqueSenders}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-white p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-purple-100 p-2.5">
              <Clock size={18} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Latest Message</p>
              <p className="text-lg font-semibold text-slate-900">
                {latestMsg?.timestamp
                  ? new Date(latestMsg.timestamp * 1000).toLocaleTimeString([], {
                      hour: '2-digit', minute: '2-digit',
                    })
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Search & Filter ── */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search messages or senders…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 bg-white rounded-xl border border-gray-200 shadow-sm text-sm outline-none focus:ring-2 focus:ring-green-300 transition"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter buttons */}
        <div className="flex items-center gap-2">
          <Filter size={15} className="text-slate-400 flex-shrink-0" />
          {['all', 'text', 'media'].map(f => (
            <button
              key={f}
              onClick={() => setFilterType(f)}
              className={`px-3 py-2 rounded-xl text-xs font-medium border transition ${
                filterType === f
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Results count ── */}
      {(search || filterType !== 'all') && (
        <p className="mb-3 text-xs text-slate-400">
          Showing {filteredLog.length} of {incomingLog.length} messages
        </p>
      )}

      {/* ── Table ── */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid grid-cols-12 gap-4 bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-3 text-xs font-semibold text-white uppercase tracking-wide">
          <div className="col-span-3 flex items-center gap-1.5"><Clock size={12} /> Timestamp</div>
          <div className="col-span-3 flex items-center gap-1.5"><User size={12} /> Sender</div>
          <div className="col-span-5">Message</div>
          <div className="col-span-1">Type</div>
        </div>

        {/* Table body */}
        <div className="divide-y divide-gray-50">
          {filteredLog.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <MessageSquare size={36} className="mx-auto mb-3 text-slate-200" />
              <p className="text-slate-400 font-medium">
                {search || filterType !== 'all'
                  ? 'No messages match your search.'
                  : 'No messages received yet.'}
              </p>
              <p className="text-slate-300 text-sm mt-1">
                {!search && filterType === 'all' && 'Messages will appear here in real-time once WhatsApp is connected.'}
              </p>
            </div>
          ) : (
            filteredLog.map((msg, idx) => {
              const isMedia = msg.text?.startsWith('[');
              const senderDisplay = msg.senderName || msg.sender || msg.from?.split('@')[0] || 'Unknown';
              const phone = msg.from?.split('@')[0] || '';

              return (
                <div
                  key={idx}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 md:px-6 py-3.5 hover:bg-slate-50 transition-colors text-sm"
                >
                  {/* Timestamp */}
                  <div className="md:col-span-3 flex items-center gap-2 text-slate-500">
                    <Clock size={12} className="md:hidden text-slate-300 flex-shrink-0" />
                    <span className="font-mono text-xs">
                      {msg.timestamp
                        ? new Date(msg.timestamp * 1000).toLocaleString([], {
                            month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : '—'}
                    </span>
                  </div>

                  {/* Sender */}
                  <div className="md:col-span-3 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {senderDisplay[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate text-xs">{senderDisplay}</p>
                      {phone && phone !== senderDisplay && (
                        <p className="text-xs text-slate-400 truncate">+{phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="md:col-span-5 text-slate-600 break-words text-sm">
                    {msg.text || <span className="italic text-slate-400">Media message</span>}
                  </div>

                  {/* Type badge */}
                  <div className="md:col-span-1 flex items-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      isMedia
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {isMedia ? 'media' : 'text'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {filteredLog.length > 0 && (
        <p className="mt-3 text-xs text-center text-slate-400">
          {filteredLog.length} message{filteredLog.length !== 1 ? 's' : ''} shown
        </p>
      )}
    </div>
  );
};

export default Reports;
